/**
 * Rate Change Mode Dialog
 *
 * Lets a user flip a contract's rate_change_mode flag between 'bulk' and
 * 'manual'. No approval workflow — audit only — but a confirm modal so the
 * change is deliberate.
 *
 *   bulk    — contract participates in benchmark sweeps
 *   manual  — contract is excluded from sweeps; rate is only changed via the
 *             per-contract adhoc rate change flow
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Alert,
  Box,
  Chip,
} from '@mui/material';
import apiClient from '../../utils/apiClient';

const MODE_LABELS = {
  bulk: 'Bulk benchmark sweep',
  manual: 'Manual rate updates only',
};

const MODE_DESCRIPTIONS = {
  bulk: 'This contract will be included in benchmark rate sweeps. Bulk rate change initiations will pick it up automatically.',
  manual: 'This contract will be excluded from benchmark sweeps. Rate changes can only be made via per-contract adhoc rate change requests.',
};

const RateChangeModeDialog = ({ open, onClose, contract, onSuccess }) => {
  const currentMode = contract?.rate_change_mode || 'bulk';
  const targetMode = currentMode === 'bulk' ? 'manual' : 'bulk';

  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason || !reason.trim()) {
      setError('A reason is required');
      return;
    }
    if (!contract?.id) {
      setError('Contract id missing');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiClient.patch(`/contracts/${contract.id}/rate-change-mode`, {
        mode: targetMode,
        reason: reason.trim(),
      });
      if (response?.data?.success) {
        if (onSuccess) onSuccess(response.data.data);
        onClose();
      } else {
        setError(response?.data?.message || 'Failed to update rate change mode');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update rate change mode');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Rate Update Mode</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" sx={{ mb: 1 }}>
          Currently:
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Chip
            label={MODE_LABELS[currentMode]}
            color={currentMode === 'manual' ? 'warning' : 'primary'}
            size="small"
          />
        </Box>

        <Typography variant="body2" sx={{ mb: 1 }}>
          Switching to:
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Chip
            label={MODE_LABELS[targetMode]}
            color={targetMode === 'manual' ? 'warning' : 'primary'}
            size="small"
          />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {MODE_DESCRIPTIONS[targetMode]}
          {' '}
          Existing pending or approved rate changes are unaffected.
        </Typography>

        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          required
          multiline
          minRows={2}
          placeholder="Why is this contract switching mode?"
          helperText="A short reason is mandatory and is captured in the audit trail."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color={targetMode === 'manual' ? 'warning' : 'primary'}
          onClick={handleSubmit}
          disabled={submitting || !reason.trim()}
        >
          {submitting ? 'Saving…' : `Switch to ${MODE_LABELS[targetMode]}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RateChangeModeDialog;
